import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import type { FeedMessage, FeedRow } from "@/types";
import { messageIdKey } from "@/utils/messageId";
import { MessageBubble } from "./MessageBubble";

interface Props {
  rows: FeedRow[];
  byId: Map<string, FeedMessage>;
  loading: boolean;
  error: string | null;
  highlightMessageId?: string | null;
  /** Прокрутить к сообщению (цитата, поиск) — отдельно от подсветки */
  focusMessageId?: string | null;
  scrollTick?: number;
  scrollToEndTick?: number;
  onGoToMessage?: (messageId: string) => void;
  firstItemIndex?: number;
  hasMoreOlder?: boolean;
  hasMoreNewer?: boolean;
  atLatest?: boolean;
  loadingMore?: boolean;
  onLoadOlder?: () => void;
  onLoadNewer?: () => void;
  onJumpToLatest?: () => void;
}

function ScrollDownIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      aria-hidden
    >
      <path d="M12 5v14M6 13l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ChatFeed({
  rows,
  byId,
  loading,
  error,
  highlightMessageId = null,
  focusMessageId = null,
  scrollTick = 0,
  scrollToEndTick = 0,
  onGoToMessage,
  firstItemIndex = 0,
  hasMoreOlder = false,
  hasMoreNewer = false,
  atLatest = true,
  loadingMore = false,
  onLoadOlder,
  onLoadNewer,
  onJumpToLatest,
}: Props) {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const initialScrollDone = useRef(false);
  const scrollLoadEnabledRef = useRef(false);
  const [atBottom, setAtBottom] = useState(true);
  const highlightKey =
    highlightMessageId != null ? messageIdKey(highlightMessageId) : null;
  const focusKey =
    focusMessageId != null ? messageIdKey(focusMessageId) : null;

  const showScrollDown = rows.length > 0 && (!atLatest || !atBottom);

  const anchorRowIndex = useMemo(() => {
    if (atLatest || highlightKey == null) return -1;
    return rows.findIndex(
      (r) => r.kind === "message" && messageIdKey(r.message.id) === highlightKey
    );
  }, [atLatest, highlightKey, rows]);

  const virtuosoKey = atLatest ? "feed-latest" : "feed-anchor";

  const enableScrollLoad = useCallback(() => {
    scrollLoadEnabledRef.current = true;
  }, []);

  useEffect(() => {
    scrollLoadEnabledRef.current = false;
  }, [virtuosoKey, highlightKey]);

  const scrollToEnd = useCallback(
    (behavior: "auto" | "smooth" = "smooth") => {
      if (rows.length === 0) return;
      virtuosoRef.current?.scrollToIndex({
        index: rows.length - 1,
        behavior,
      });
    },
    [rows.length]
  );

  useEffect(() => {
    if (loading || rows.length === 0 || !atLatest) return;
    if (initialScrollDone.current) return;
    initialScrollDone.current = true;
    requestAnimationFrame(() => scrollToEnd("auto"));
  }, [loading, rows.length, atLatest, scrollToEnd]);

  useEffect(() => {
    if (!atLatest) {
      initialScrollDone.current = false;
    }
  }, [atLatest]);

  useEffect(() => {
    if (scrollTick <= 0 || rows.length === 0 || !atLatest) return;
    scrollToEnd("smooth");
  }, [scrollTick, atLatest, scrollToEnd]);

  useEffect(() => {
    if (scrollToEndTick <= 0 || rows.length === 0) return;
    scrollLoadEnabledRef.current = false;
    requestAnimationFrame(() => scrollToEnd("smooth"));
  }, [scrollToEndTick, scrollToEnd]);

  useEffect(() => {
    if (focusKey == null || rows.length === 0) return;
    const idx = rows.findIndex(
      (r) => r.kind === "message" && messageIdKey(r.message.id) === focusKey
    );
    if (idx < 0) return;

    let cancelled = false;
    const scrollToFocus = () => {
      if (cancelled) return;
      virtuosoRef.current?.scrollToIndex({
        index: idx,
        align: "center",
        behavior: "smooth",
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(scrollToFocus));
    return () => {
      cancelled = true;
    };
  }, [focusKey, rows, virtuosoKey]);

  const initialTopIndex =
    !atLatest && anchorRowIndex >= 0
      ? anchorRowIndex
      : Math.max(0, rows.length - 1);

  if (loading) {
    return (
      <div className="feed-state">
        <div className="feed-state-icon" aria-hidden>
          ◌
        </div>
        <p>Загружаем архив обсуждений…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-state feed-state--error">
        <div className="feed-state-icon" aria-hidden>
          !
        </div>
        <p>{error}</p>
        <code>python scripts/import_feed_jsonl.py</code>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="feed-state">
        <div className="feed-state-icon" aria-hidden>
          💬
        </div>
        <p>Архив пока пуст</p>
      </div>
    );
  }

  return (
    <div
      className="chat-feed"
      onWheel={enableScrollLoad}
      onTouchMove={enableScrollLoad}
    >
      {loadingMore && hasMoreOlder && (
        <div className="feed-load-more feed-load-more--top" aria-live="polite">
          Загружаем более ранние сообщения…
        </div>
      )}
      {loadingMore && hasMoreNewer && (
        <div
          className="feed-load-more feed-load-more--bottom"
          aria-live="polite"
        >
          Загружаем более новые сообщения…
        </div>
      )}
      <Virtuoso
        key={virtuosoKey}
        ref={virtuosoRef}
        data={rows}
        firstItemIndex={firstItemIndex}
        style={{ height: "100%" }}
        initialTopMostItemIndex={initialTopIndex}
        alignToBottom={atLatest}
        followOutput={atLatest ? "smooth" : false}
        atBottomStateChange={setAtBottom}
        increaseViewportBy={{ top: 400, bottom: 400 }}
        startReached={() => {
          if (!scrollLoadEnabledRef.current) return;
          if (hasMoreOlder && !loadingMore) onLoadOlder?.();
        }}
        endReached={() => {
          if (!scrollLoadEnabledRef.current) return;
          if (hasMoreNewer && !loadingMore) onLoadNewer?.();
        }}
        className="chat-feed-virtuoso"
        itemContent={(_index, row) => {
          if (row.kind === "date") {
            return (
              <div className="date-separator" key={row.key}>
                <span>{row.label}</span>
              </div>
            );
          }

          const msg = row.message;
          const reply = msg.reply_to
            ? byId.get(messageIdKey(msg.reply_to))
            : undefined;
          const highlighted =
            highlightKey != null && messageIdKey(msg.id) === highlightKey;

          return (
            <div
              className={`message-row${msg.is_live ? " message-row--live" : ""}`}
              key={row.key}
            >
              <MessageBubble
                message={msg}
                replyTo={reply}
                highlighted={highlighted}
                onGoToMessage={onGoToMessage}
              />
            </div>
          );
        }}
      />
      {showScrollDown && (
        <button
          type="button"
          className="feed-scroll-down"
          onClick={() => onJumpToLatest?.()}
          aria-label="К последним сообщениям"
          title="К последним сообщениям"
        >
          <ScrollDownIcon />
        </button>
      )}
    </div>
  );
}
